import { useCallback, useEffect, useRef, useState } from 'react'

interface PanelCoords {
    bottom: number
    left: number
    width?: number
}

/**
 * Shared positioning for the sidebar's low-in-the-column popovers/panels
 * (`SidebarNotificationButton`, `SidebarQuotaWidget`'s breakdown panel) — both
 * sit near the bottom of the sidebar, so opening downward like a typical
 * dropdown leaves little to no room and collides with whatever's below.
 * `mode: 'above'` bottom-anchors the panel so it opens upward, pinned to the
 * trigger regardless of the panel's own content height; `mode: 'right'`
 * anchors it beside the trigger instead, for the collapsed icon rail.
 *
 * Deliberately NOT `features/box-truck-loads/hooks/useAnchoredPopover` — that
 * hook always opens *below* the trigger (flipping above only when there's no
 * room), types its trigger ref as `HTMLButtonElement` (these triggers are
 * plain `div`s), and is built around click-driven open/close. This one is
 * generic over the trigger element and doesn't presume how `open` toggles —
 * `SidebarQuotaWidget` drives it from hover, `SidebarNotificationButton` from
 * a click — so it fits both without forcing either into the other's shape.
 *
 * Caller owns the `open` boolean's lifecycle (click, hover, whatever) and
 * just calls `computeCoords()` once right before flipping it true.
 */
export const useSidebarAnchoredPanel = <T extends HTMLElement>(mode: 'above' | 'right', open: boolean, panelWidth?: number) => {
    const [coords, setCoords] = useState<PanelCoords | null>(null)
    const triggerRef = useRef<T>(null)

    const computeCoords = useCallback(() => {
        if (!triggerRef.current) return
        const rect = triggerRef.current.getBoundingClientRect()
        if (mode === 'above') {
            const width = panelWidth ?? rect.width
            setCoords({
                bottom: window.innerHeight - rect.top + 8,
                // Clamp both edges so a panel wider than the viewport (or a trigger near an edge)
                // can't render partially off-screen.
                left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
                width,
            })
        } else {
            setCoords({ bottom: window.innerHeight - rect.bottom, left: rect.right + 8 })
        }
    }, [mode, panelWidth])

    // Recompute on resize/scroll while open instead of once at open time — otherwise the
    // fixed-position panel detaches from its trigger if the window resizes or an ancestor
    // scrolls while it's visible. `capture: true` on scroll catches ancestor scrolling too.
    useEffect(() => {
        if (!open) return
        window.addEventListener('resize', computeCoords)
        window.addEventListener('scroll', computeCoords, true)
        return () => {
            window.removeEventListener('resize', computeCoords)
            window.removeEventListener('scroll', computeCoords, true)
        }
    }, [open, computeCoords])

    return { coords, triggerRef, computeCoords }
}
